import { styled, useTheme } from '@mui/material/styles';
import { Icon, Typography } from '../../../atoms';
import { NavLink } from '../Link';
import { NavDropdown } from '../Dropdown';
import { useTranslation } from 'react-i18next';
import { ReactComponent as AdminNav } from '../../../../assets/icons/adminNav.svg';
import { ReactComponent as LogoutNav } from '../../../../assets/icons/logoutNav.svg';

const Container = styled('span')<{ isActive: boolean }>(({ isActive = false }) => ({
    display: 'flex',
    alignItems: 'center',
    '& > .person-icon': {
        marginRight: '5px',
        width: '16px',
        height: '16px'
    },
    '& > .chevron-icon': {
        width: '15px',
        height: '10px',
        ...(isActive && {
            transform: 'rotate(180deg)'
        })
    }
}));

export const Name = styled('span')(() => ({
    marginRight: '8px',
    textTransform: 'uppercase'
}));

const NavDropdownName = styled('span')(({ theme }) => ({
    color: theme.palette.semantic.icon.default,
    width: '94px',
    height: '20px',
    marginBottom: '18px'
}));

const DropdownButton = styled('button')(({ theme }) => ({
    color: theme.palette.semantic.int.selected,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    marginRight: 'auto',
    padding: 0,
    '&:not(:last-child)': {
        marginBottom: '8px'
    }
}));

const DropdownLegend = styled('span')(({ theme }) => ({
    fontFamily: 'TT Commons Pro',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    color: theme.palette.semantic.text.muted
}));

const AdminControls = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '40px auto',
    columnGap: '8px',
    rowGap: '2px',
    alignItems: 'center',
    marginBottom: '18px',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: theme.palette.neutral[50],
        borderRadius: '8px'
    }
}));

const AdminIconCell = styled('div')(() => ({
    gridRow: '1 / span 2'
}));

const MenuItemControls = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '40px auto',
    columnGap: '8px',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: theme.palette.neutral[50],
        borderRadius: '8px'
    }
}));

const MenuItemButton = styled(DropdownButton)(() => ({
    marginRight: 0,
    '&:not(:last-child)': {
        marginBottom: 0
    }
}));

const StyledHeaderIconContainer = styled('div')<{ disabled?: boolean }>(({ theme }) => ({
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    '& path:first-of-type': {
        fill: theme.palette.semantic.bg.brandLight2,
        stroke: 'none'
    },
    '& path[stroke]': {
        stroke: theme.palette.semantic.icon.brand
    }
}));

type Props = {
    loggedInUsername: string;
    userRole?: string;
    tenantId?: string;
    tenant?: string;
    isHantzTenant: boolean;
    adminTitle?: string;
    isDropdownActive: boolean;
    onToggleDropdown(): void;
    onClickAdmin(): void;
    onClickLogout(): void;
};

export const NavUser = ({
    loggedInUsername,
    userRole,
    isHantzTenant,
    isDropdownActive,
    onToggleDropdown,
    onClickAdmin,
    onClickLogout
}: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const handleLogoutClick = () => {
        onClickLogout();
        onToggleDropdown();
    };

    const handleAdminClick = () => {
        onClickAdmin();
        onToggleDropdown();
    };

    return (
        <>
            <NavLink onClick={onToggleDropdown} id="nav-user-button" aria-expanded={isDropdownActive}>
                <Container isActive={isDropdownActive}>
                    <Icon id="person" className="person-icon" />
                    <Name>{loggedInUsername}</Name>
                    <Icon id="chevron-down" className="chevron-icon" />
                </Container>
            </NavLink>
            <NavDropdown isVisible={isDropdownActive} parentId="nav-user-button" onClose={onToggleDropdown}>
                {isHantzTenant ? null : userRole === 'admin' || userRole === 'ftadmin' ? (
                    <>
                        <NavDropdownName>
                            <Typography
                                variant="labels.large"
                                label={loggedInUsername}
                                sx={{ color: theme.palette.semantic.icon.default }}
                            />
                        </NavDropdownName>
                        <AdminControls onClick={handleAdminClick}>
                            <AdminIconCell>
                                <StyledHeaderIconContainer>
                                    <AdminNav />
                                </StyledHeaderIconContainer>
                            </AdminIconCell>
                            <MenuItemButton type="button">
                                <Typography
                                    variant="labels.largeCondensedCaps"
                                    label={t(userRole.toUpperCase())}
                                    sx={{ color: theme.palette.semantic.text.brand }}
                                />
                            </MenuItemButton>
                            <DropdownLegend>{t('TEXT_TENANT_ADMIN')}</DropdownLegend>
                        </AdminControls>
                    </>
                ) : null}
                <MenuItemControls onClick={handleLogoutClick}>
                    <StyledHeaderIconContainer>
                        <LogoutNav />
                    </StyledHeaderIconContainer>
                    <MenuItemButton type="button" aria-haspopup="dialog">
                        <Typography
                            variant="labels.largeCondensedCaps"
                            label={t('TEXT_MENU_LOGOUT')}
                            sx={{ color: theme.palette.semantic.int.selected }}
                        />
                    </MenuItemButton>
                </MenuItemControls>
            </NavDropdown>
        </>
    );
};
